import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import {
  Globe,
  Shield,
  Bot,
  Network,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Info,
  Plus,
  X,
} from 'lucide-react'
import type { Site } from './shared'

interface GeoBlockingTabProps {
  site: Site
}

// Country list (common countries to block)
const COUNTRIES = [
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'KP', name: 'North Korea', flag: '🇰🇵' },
  { code: 'IR', name: 'Iran', flag: '🇮🇷' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
] as const

// Default bot patterns
const DEFAULT_BOTS = [
  { id: 'Googlebot', label: 'Googlebot' },
  { id: 'Bingbot', label: 'Bingbot' },
  { id: 'Slurp', label: 'Yahoo Slurp' },
  { id: 'DuckDuckBot', label: 'DuckDuckBot' },
  { id: 'facebookexternalhit', label: 'Facebook' },
  { id: 'Twitterbot', label: 'Twitter/X' },
  { id: 'LinkedInBot', label: 'LinkedIn' },
]

interface GeoBlockConfig {
  enabled: boolean
  mode: 'block' | 'allow'
  countries: string[]
  // Legacy field name (for backward compatibility)
  blocked_countries?: string[]
  bot_whitelist: string[]
  ip_whitelist: string[]
  block_message: string
}

interface GeoBlockStatus {
  cloudflare: {
    is_cloudflare: boolean
    cf_ipcountry: string | null
    cf_connecting_ip: string | null
    cf_ray: string | null
  }
  mu_installed: boolean
  enabled: boolean
  blocked_count: number
}

interface GeoBlockConfigResponse {
  success: boolean
  config: GeoBlockConfig
  mu_installed: boolean
  presets?: {
    bots: string[]
    ip_ranges: Record<string, { label: string; ranges: string[] }>
  }
}

export function GeoBlockingTab({ site }: GeoBlockingTabProps) {
  const queryClient = useQueryClient()

  // Local state for form
  const [enabled, setEnabled] = useState(false)
  const [mode, setMode] = useState<'block' | 'allow'>('block')
  const [countries, setCountries] = useState<string[]>([])
  const [botWhitelist, setBotWhitelist] = useState<string[]>([])
  const [ipWhitelist, setIpWhitelist] = useState('')
  const [blockMessage, setBlockMessage] = useState('Site Unavailable')
  const [customCountry, setCustomCountry] = useState('')

  // Fetch current config
  const {
    data: configData,
    isLoading: configLoading,
    error: configError,
  } = useQuery<GeoBlockConfigResponse>({
    queryKey: ['geo-block-config', site.domain],
    queryFn: async () => {
      const res = await fetch(`/api/auth-server/sites/${encodeURIComponent(site.domain)}/remote/geo-block/config`)
      if (!res.ok) throw new Error('Failed to fetch geo-block config')
      return res.json()
    },
    retry: 1,
  })

  // Fetch Cloudflare status
  const { data: statusData } = useQuery<{ success: boolean } & GeoBlockStatus>({
    queryKey: ['geo-block-status', site.domain],
    queryFn: async () => {
      const res = await fetch(`/api/auth-server/sites/${encodeURIComponent(site.domain)}/remote/geo-block/status`)
      if (!res.ok) throw new Error('Failed to fetch geo-block status')
      return res.json()
    },
    retry: 1,
  })

  // Sync local state when data loads
  useEffect(() => {
    if (configData?.config) {
      setEnabled(configData.config.enabled)
      setMode(configData.config.mode || 'block')
      // Support both new 'countries' and legacy 'blocked_countries' field
      const countryList = configData.config.countries || configData.config.blocked_countries || []
      setCountries(countryList)
      setBotWhitelist(configData.config.bot_whitelist || [])
      setIpWhitelist((configData.config.ip_whitelist || []).join('\n'))
      setBlockMessage(configData.config.block_message || 'Site Unavailable')
    }
  }, [configData])

  // Update config mutation
  const updateConfig = useMutation({
    mutationFn: async (config: Partial<GeoBlockConfig>) => {
      const res = await fetch(`/api/auth-server/sites/${encodeURIComponent(site.domain)}/remote/geo-block/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      })
      if (!res.ok) throw new Error('Failed to update config')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geo-block-config', site.domain] })
      toast.success('Geo-blocking configuration saved')
    },
    onError: (err) => {
      toast.error(`Failed to save: ${String(err)}`)
    },
  })

  const handleSave = () => {
    const ipList = ipWhitelist
      .split('\n')
      .map((ip) => ip.trim())
      .filter((ip) => ip.length > 0)

    updateConfig.mutate({
      enabled,
      mode,
      countries,
      bot_whitelist: botWhitelist,
      ip_whitelist: ipList,
      block_message: blockMessage,
    })
  }

  const toggleCountry = (code: string) => {
    setCountries((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    )
  }

  const addCustomCountry = () => {
    const code = customCountry.toUpperCase().trim()
    if (code.length === 2 && !countries.includes(code)) {
      setCountries((prev) => [...prev, code])
      setCustomCountry('')
    }
  }

  const toggleBot = (botId: string, checked: boolean) => {
    setBotWhitelist((prev) => (checked ? [...prev, botId] : prev.filter((b) => b !== botId)))
  }

  const addBotIPs = (provider: string) => {
    const presets = configData?.presets?.ip_ranges
    if (presets && presets[provider]) {
      const newIPs = presets[provider].ranges
      const currentIPs = ipWhitelist.split('\n').filter((ip) => ip.trim())
      const combined = [...new Set([...currentIPs, ...newIPs])]
      setIpWhitelist(combined.join('\n'))
      toast.success(`Added ${presets[provider].label} IP ranges`)
    }
  }

  // Loading state
  if (configLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Error state - likely needs jezweb-geo-block plugin
  if (configError) {
    return (
      <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
          <div>
            <div className="font-medium text-yellow-700 dark:text-yellow-400">
              Geo-Blocking Not Available
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              This site requires the Jezweb Geo-Block plugin for geo-blocking features.
              Install the plugin to enable this functionality.
            </div>
          </div>
        </div>
      </div>
    )
  }

  const isCloudflare = statusData?.cloudflare?.is_cloudflare ?? false

  return (
    <div className="space-y-6">
      {/* Cloudflare Status Banner */}
      {isCloudflare ? (
        <div className="rounded-lg border p-4 border-green-500/50 bg-green-500/10">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
            <div>
              <div className="font-medium text-green-700 dark:text-green-400">
                Cloudflare Headers Detected
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Country code: <code className="px-1 bg-muted rounded">{statusData?.cloudflare?.cf_ipcountry || 'N/A'}</code>
                {' - '}Geo-blocking will work with zero performance impact.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border p-4 border-yellow-500/50 bg-yellow-500/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
            <div>
              <div className="font-medium text-yellow-700 dark:text-yellow-400">
                Cloudflare Proxy Not Detected
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                <strong>Geo-blocking will not work</strong> without Cloudflare's CF-IPCountry header.
              </div>
              <div className="text-sm text-muted-foreground mt-2">
                To fix: Go to Cloudflare DNS settings and enable the <strong>orange cloud</strong> (Proxy status: Proxied) for this domain's A/AAAA records.
              </div>
              <div className="text-xs text-muted-foreground mt-2 opacity-70">
                Note: This check uses API requests which may show false negatives if the site selectively proxies traffic.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enable Toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg border">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-muted-foreground" />
          <div>
            <Label htmlFor="geo-enable" className="font-medium">
              Enable Geo-Blocking
            </Label>
            <div className="text-sm text-muted-foreground">
              Restrict site access based on visitor country
            </div>
          </div>
        </div>
        <Switch
          id="geo-enable"
          checked={enabled}
          onCheckedChange={setEnabled}
          disabled={updateConfig.isPending}
        />
      </div>

      {enabled && (
        <>
          {/* Warning if enabled but no Cloudflare */}
          {!isCloudflare && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3">
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  <strong>Warning:</strong> These settings will be saved but geo-blocking won't work until Cloudflare proxy is enabled.
                </span>
              </div>
            </div>
          )}

          {/* Mode Toggle */}
          <div className="space-y-3 p-4 rounded-lg border">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Blocking Mode</span>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="geo-mode"
                  value="block"
                  checked={mode === 'block'}
                  onChange={() => setMode('block')}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-medium text-sm">Block countries</div>
                  <div className="text-xs text-muted-foreground">Block visitors from selected countries</div>
                </div>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="geo-mode"
                  value="allow"
                  checked={mode === 'allow'}
                  onChange={() => setMode('allow')}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-medium text-sm">Allow only countries</div>
                  <div className="text-xs text-muted-foreground">Only allow visitors from selected countries</div>
                </div>
              </label>
            </div>
          </div>

          {/* Countries Selection */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">
                {mode === 'block' ? 'Blocked Countries' : 'Allowed Countries'}
              </span>
            </div>
            <div className="text-sm text-muted-foreground">
              {mode === 'block'
                ? 'Select countries to block. Visitors from these countries will see the block message.'
                : 'Select countries to allow. Visitors from all other countries will be blocked.'}
            </div>

            {/* Selected countries badges */}
            {countries.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {countries.map((code) => {
                  const country = COUNTRIES.find((c) => c.code === code)
                  return (
                    <Badge key={code} variant="secondary" className="gap-1">
                      {country?.flag || '🏳️'} {country?.name || code}
                      <button
                        onClick={() => toggleCountry(code)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )
                })}
              </div>
            )}

            {/* Country grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {COUNTRIES.map((country) => (
                <div
                  key={country.code}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                    countries.includes(country.code)
                      ? 'border-primary bg-primary/10'
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => toggleCountry(country.code)}
                >
                  <Checkbox
                    checked={countries.includes(country.code)}
                    onCheckedChange={() => toggleCountry(country.code)}
                  />
                  <span className="text-sm">
                    {country.flag} {country.name}
                  </span>
                </div>
              ))}
            </div>

            {/* Custom country input */}
            <div className="flex gap-2">
              <Input
                placeholder="Custom code (e.g., AF)"
                value={customCountry}
                onChange={(e) => setCustomCountry(e.target.value.slice(0, 2))}
                className="w-40"
              />
              <Button size="sm" variant="outline" onClick={addCustomCountry}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Bot Whitelist */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Search Engine Bot Exceptions</span>
            </div>
            <div className="text-sm text-muted-foreground">
              Allow these bots even from blocked countries (detected by User-Agent).
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {DEFAULT_BOTS.map((bot) => (
                <div key={bot.id} className="flex items-center gap-2 p-2 rounded border">
                  <Checkbox
                    id={`bot-${bot.id}`}
                    checked={botWhitelist.includes(bot.id)}
                    onCheckedChange={(checked) => toggleBot(bot.id, checked === true)}
                  />
                  <Label htmlFor={`bot-${bot.id}`} className="font-normal text-sm cursor-pointer">
                    {bot.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* IP Whitelist */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Network className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">IP Whitelist</span>
            </div>
            <div className="text-sm text-muted-foreground">
              Allow specific IPs or CIDR ranges even from blocked countries. One per line.
            </div>
            <Textarea
              value={ipWhitelist}
              onChange={(e) => setIpWhitelist(e.target.value)}
              placeholder="66.249.64.0/19&#10;203.59.186.15"
              rows={4}
              className="font-mono text-sm"
            />
            {configData?.presets?.ip_ranges && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(configData.presets.ip_ranges).map(([key, data]) => (
                  <Button
                    key={key}
                    size="sm"
                    variant="outline"
                    onClick={() => addBotIPs(key)}
                  >
                    <Plus className="h-3 w-3 mr-1" /> {data.label} IPs
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Block Message */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Block Message</span>
            </div>
            <div className="text-sm text-muted-foreground">
              Message shown to blocked visitors.
            </div>
            <Input
              value={blockMessage}
              onChange={(e) => setBlockMessage(e.target.value)}
              placeholder="Site Unavailable"
            />
          </div>
        </>
      )}

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={updateConfig.isPending}>
          {updateConfig.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save Changes
        </Button>
      </div>
    </div>
  )
}
